import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = { name: "", description: "", image: null };

const AwardFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { awardId } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState("");

  const isEditMode = mode === "edit";
  const pageTitle = useMemo(() => (isEditMode ? "Edit Award" : "Create Award"), [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setIsPageLoading(false);
      return;
    }

    const fetchAward = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/awards/${awardId}`);
        const item = response.data?.data;
        if (!item) {
          showErrorToast("Award not found");
          navigate("/awards");
          return;
        }
        setForm({ name: item.name || "", description: item.description || "", image: null });
        setImagePreviewUrl(item.image_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load award");
        navigate("/awards");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchAward();
  }, [awardId, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Name is required.";
    if (!form.description.trim()) nextErrors.description = "Description is required.";
    if (!isEditMode && !form.image) nextErrors.image = "Image is required.";
    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("description", form.description.trim());
      if (form.image) payload.append("image", form.image);

      if (isEditMode) {
        await apiClient.put(`/awards/${awardId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Award updated successfully");
      } else {
        await apiClient.post("/awards", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Award created successfully");
      }

      navigate("/awards");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save award");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading award editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/awards")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Awards
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">{pageTitle}</h1>
      </div>

      <form onSubmit={handleSave} className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Name</label>
              <Input value={form.name} onChange={(event) => handleInputChange("name", event.target.value)} className={`rounded-lg ${formErrors.name ? "border-red-300 focus-visible:ring-red-500" : ""}`} />
              <FieldError>{formErrors.name}</FieldError>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Description</label>
              <textarea value={form.description} onChange={(event) => handleInputChange("description", event.target.value)} rows={8} className={`min-h-[180px] w-full rounded-lg border bg-white px-4 py-3 text-sm text-slate-700 outline-none ${formErrors.description ? "border-red-300 focus:ring-2 focus:ring-red-500" : "border-slate-200 focus:ring-2 focus:ring-primary/30"}`} />
              <FieldError>{formErrors.description}</FieldError>
            </div>
          </div>
          <div className="space-y-2">
            <InlineFormFileUpload
              label="Image"
              value={form.image}
              onChange={(file) => handleInputChange("image", file)}
              existingFiles={imagePreviewUrl ? [imagePreviewUrl] : []}
              accept={IMAGE_TYPES}
              maxSizeBytes={10 * 1024 * 1024}
              required={!isEditMode}
              error={formErrors.image}
              helperText="JPG, PNG, GIF or WEBP — maximum 10 MB"
            />
            <ImageUploadGuidance requirementKey="awardImage" file={form.image} src={imagePreviewUrl} />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/awards")}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Award" : "Create Award"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AwardFormPage;
